import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Mail, CalendarDays, Trash2, RefreshCw, Inbox, Newspaper, Plus, Pencil, X, Briefcase, FileDown } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { COMPANY } from "../content";
import { TOKEN_KEY } from "./AdminLogin";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  pending: { label: "En attente", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  confirmed: { label: "Confirmé", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelled: { label: "Annulé", cls: "bg-red-100 text-red-700 border-red-200" },
};

const EMPTY_ARTICLE = { title: "", category: "", image: "", content: "" };

const formatDateTime = (iso) => {
  try {
    return format(new Date(iso), "d MMM yyyy 'à' HH:mm", { locale: fr });
  } catch {
    return iso;
  }
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [articles, setArticles] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [articleForm, setArticleForm] = useState(EMPTY_ARTICLE);
  const [editingId, setEditingId] = useState(null);
  const [savingArticle, setSavingArticle] = useState(false);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    return { Authorization: `Bearer ${token}` };
  }, []);

  const handleAuthError = useCallback(
    (err) => {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        toast.error("Session expirée. Veuillez vous reconnecter.");
        navigate("/admin");
        return true;
      }
      return false;
    },
    [navigate]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [msgRes, aptRes, artRes, appRes] = await Promise.all([
        axios.get(`${API}/contact`, { headers: authHeaders() }),
        axios.get(`${API}/appointments`, { headers: authHeaders() }),
        axios.get(`${API}/articles`),
        axios.get(`${API}/careers`, { headers: authHeaders() }),
      ]);
      setMessages(msgRes.data);
      setAppointments(aptRes.data);
      setArticles(artRes.data);
      setApplications(appRes.data);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Erreur lors du chargement des données.");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, handleAuthError]);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [fetchData, navigate]);

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    navigate("/admin");
  };

  const deleteMessage = async (id) => {
    try {
      await axios.delete(`${API}/contact/${id}`, { headers: authHeaders() });
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success("Message supprimé.");
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Suppression impossible.");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await axios.patch(`${API}/appointments/${id}`, { status }, { headers: authHeaders() });
      setAppointments((prev) => prev.map((a) => (a.id === id ? res.data : a)));
      const extra = status === "confirmed" || status === "cancelled" ? " Un email a été envoyé au client." : "";
      toast.success(`Rendez-vous ${STATUS_LABELS[status].label.toLowerCase()}.${extra}`);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Mise à jour impossible.");
    }
  };

  const deleteApplication = async (id) => {
    try {
      await axios.delete(`${API}/careers/${id}`, { headers: authHeaders() });
      setApplications((prev) => prev.filter((a) => a.id !== id));
      toast.success("Candidature supprimée.");
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Suppression impossible.");
    }
  };

  const downloadCv = async (application) => {
    try {
      const res = await axios.get(`${API}/careers/${application.id}/cv`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = application.cv_original || "cv.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Téléchargement du CV impossible.");
    }
  };

  // ===== Articles =====
  const openCreateArticle = () => {
    setArticleForm(EMPTY_ARTICLE);
    setEditingId(null);
    setShowArticleForm(true);
  };

  const openEditArticle = (a) => {
    setArticleForm({ title: a.title, category: a.category || "", image: a.image || "", content: a.content });
    setEditingId(a.id);
    setShowArticleForm(true);
  };

  const saveArticle = async (e) => {
    e.preventDefault();
    if (!articleForm.title.trim() || !articleForm.content.trim()) {
      toast.error("Le titre et le contenu sont obligatoires.");
      return;
    }
    setSavingArticle(true);
    try {
      if (editingId) {
        const res = await axios.put(`${API}/articles/${editingId}`, articleForm, { headers: authHeaders() });
        setArticles((prev) => prev.map((a) => (a.id === editingId ? res.data : a)));
        toast.success("Article mis à jour.");
      } else {
        const res = await axios.post(`${API}/articles`, articleForm, { headers: authHeaders() });
        setArticles((prev) => [res.data, ...prev]);
        toast.success("Article publié !");
      }
      setShowArticleForm(false);
      setArticleForm(EMPTY_ARTICLE);
      setEditingId(null);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Enregistrement impossible.");
    } finally {
      setSavingArticle(false);
    }
  };

  const deleteArticle = async (id) => {
    try {
      await axios.delete(`${API}/articles/${id}`, { headers: authHeaders() });
      setArticles((prev) => prev.filter((a) => a.id !== id));
      toast.success("Article supprimé.");
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Suppression impossible.");
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f7fb] pt-20" data-testid="admin-dashboard">
      {/* Barre admin */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-[#1b2653]">Tableau de bord</h1>
            <p className="text-slate-500 text-sm font-light mt-1">Espace administration — {COMPANY.fullName}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchData}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 border border-slate-300 text-[#1b2653] text-sm font-medium hover:border-[#2f6fd8] hover:text-[#2f6fd8] transition-colors duration-300"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw size={15} /> Actualiser
            </button>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[#1b2653] text-white text-sm font-medium hover:bg-[#2f6fd8] transition-colors duration-300"
              data-testid="admin-logout-btn"
            >
              <LogOut size={15} /> Déconnexion
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <Tabs defaultValue="messages">
          <TabsList className="bg-white border border-slate-200 h-12 p-1 flex-wrap">
            <TabsTrigger value="messages" className="px-5 h-full data-[state=active]:bg-[#1b2653] data-[state=active]:text-white" data-testid="tab-messages">
              <Mail size={16} className="mr-2" /> Messages ({messages.length})
            </TabsTrigger>
            <TabsTrigger value="appointments" className="px-5 h-full data-[state=active]:bg-[#1b2653] data-[state=active]:text-white" data-testid="tab-appointments">
              <CalendarDays size={16} className="mr-2" /> Rendez-vous ({appointments.length})
            </TabsTrigger>
            <TabsTrigger value="articles" className="px-5 h-full data-[state=active]:bg-[#1b2653] data-[state=active]:text-white" data-testid="tab-articles">
              <Newspaper size={16} className="mr-2" /> Actualités ({articles.length})
            </TabsTrigger>
            <TabsTrigger value="careers" className="px-5 h-full data-[state=active]:bg-[#1b2653] data-[state=active]:text-white" data-testid="tab-careers">
              <Briefcase size={16} className="mr-2" /> Candidatures ({applications.length})
            </TabsTrigger>
          </TabsList>

          {/* MESSAGES */}
          <TabsContent value="messages" className="mt-8">
            {loading ? (
              <p className="text-slate-500 font-light">Chargement...</p>
            ) : messages.length === 0 ? (
              <div className="bg-white rounded-lg border border-slate-200 p-14 text-center">
                <Inbox size={40} className="text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 font-light">Aucun message reçu pour le moment.</p>
              </div>
            ) : (
              <div className="space-y-5" data-testid="messages-list">
                {messages.map((m) => (
                  <div key={m.id} className="bg-white rounded-lg border border-slate-200 p-6 hover:shadow-md transition-shadow duration-300">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <span className="font-semibold text-[#1b2653]">{m.name}</span>
                          {m.subject && (
                            <Badge variant="outline" className="border-[#2f6fd8]/30 text-[#2547a8] bg-[#eef3fc]">{m.subject}</Badge>
                          )}
                          <span className="text-slate-400 text-xs">{formatDateTime(m.created_at)}</span>
                        </div>
                        <div className="text-sm text-slate-500 mb-3">
                          <a href={`mailto:${m.email}`} className="hover:text-[#2547a8] transition-colors">{m.email}</a>
                          {m.phone && <span> • {m.phone}</span>}
                        </div>
                        <p className="text-slate-700 text-[14.5px] leading-relaxed font-light whitespace-pre-wrap">{m.message}</p>
                      </div>
                      <button
                        onClick={() => deleteMessage(m.id)}
                        className="shrink-0 p-2.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors duration-300"
                        aria-label="Supprimer"
                        data-testid={`delete-message-${m.id}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* RENDEZ-VOUS */}
          <TabsContent value="appointments" className="mt-8">
            {loading ? (
              <p className="text-slate-500 font-light">Chargement...</p>
            ) : appointments.length === 0 ? (
              <div className="bg-white rounded-lg border border-slate-200 p-14 text-center">
                <CalendarDays size={40} className="text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 font-light">Aucun rendez-vous pour le moment.</p>
              </div>
            ) : (
              <div className="space-y-5" data-testid="appointments-list">
                {appointments.map((a) => {
                  const st = STATUS_LABELS[a.status] || STATUS_LABELS.pending;
                  return (
                    <div key={a.id} className="bg-white rounded-lg border border-slate-200 p-6 hover:shadow-md transition-shadow duration-300">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-3 mb-2">
                            <span className="font-semibold text-[#1b2653]">{a.name}</span>
                            <Badge variant="outline" className={`border ${st.cls}`}>{st.label}</Badge>
                            <Badge variant="outline" className="border-[#2f6fd8]/30 text-[#2547a8] bg-[#eef3fc]">{a.service}</Badge>
                          </div>
                          <div className="text-sm text-slate-600 font-medium mb-1">
                            {format(new Date(a.date + "T00:00:00"), "EEEE d MMMM yyyy", { locale: fr })} à {a.time_slot}
                          </div>
                          <div className="text-sm text-slate-500">
                            <a href={`mailto:${a.email}`} className="hover:text-[#2547a8] transition-colors">{a.email}</a>
                            {a.phone && <span> • {a.phone}</span>}
                          </div>
                          {a.notes && (
                            <p className="text-slate-600 text-sm font-light mt-2 whitespace-pre-wrap">{a.notes}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          {a.status !== "confirmed" && (
                            <button
                              onClick={() => updateStatus(a.id, "confirmed")}
                              className="rounded-full px-5 py-2 bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors duration-300"
                              data-testid={`confirm-appointment-${a.id}`}
                            >
                              Confirmer
                            </button>
                          )}
                          {a.status !== "cancelled" && (
                            <button
                              onClick={() => updateStatus(a.id, "cancelled")}
                              className="rounded-full px-5 py-2 border border-red-300 text-red-600 text-sm font-medium hover:bg-red-50 transition-colors duration-300"
                              data-testid={`cancel-appointment-${a.id}`}
                            >
                              Annuler
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ACTUALITÉS */}
          <TabsContent value="articles" className="mt-8">
            <div className="flex items-center justify-between mb-6">
              <p className="text-slate-500 text-sm font-light">
                Publiez vos conseils fiscaux et nouveautés comptables — visibles sur la page Actualités du site.
              </p>
              <button
                onClick={openCreateArticle}
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[#1b2653] text-white text-sm font-medium hover:bg-[#2f6fd8] transition-colors duration-300 shrink-0"
                data-testid="new-article-btn"
              >
                <Plus size={16} /> Nouvel article
              </button>
            </div>

            {/* Formulaire article */}
            {showArticleForm && (
              <form onSubmit={saveArticle} className="bg-white rounded-lg border border-[#2f6fd8]/40 p-7 mb-8 shadow-md" data-testid="article-form">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-[#1b2653]">
                    {editingId ? "Modifier l'article" : "Nouvel article"}
                  </h3>
                  <button type="button" onClick={() => setShowArticleForm(false)} className="p-2 text-slate-400 hover:text-slate-700" aria-label="Fermer">
                    <X size={18} />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <Label className="text-[#1b2653] text-sm">Titre *</Label>
                    <Input
                      value={articleForm.title}
                      onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                      placeholder="Titre de l'article"
                      className="mt-2 h-11"
                      data-testid="article-input-title"
                    />
                  </div>
                  <div>
                    <Label className="text-[#1b2653] text-sm">Catégorie</Label>
                    <Input
                      value={articleForm.category}
                      onChange={(e) => setArticleForm({ ...articleForm, category: e.target.value })}
                      placeholder="Ex : Fiscalité, Comptabilité..."
                      className="mt-2 h-11"
                      data-testid="article-input-category"
                    />
                  </div>
                </div>
                <div className="mb-5">
                  <Label className="text-[#1b2653] text-sm">Image (URL, optionnel)</Label>
                  <Input
                    value={articleForm.image}
                    onChange={(e) => setArticleForm({ ...articleForm, image: e.target.value })}
                    placeholder="https://..."
                    className="mt-2 h-11"
                    data-testid="article-input-image"
                  />
                </div>
                <div className="mb-6">
                  <Label className="text-[#1b2653] text-sm">Contenu * (séparez les paragraphes par une ligne vide)</Label>
                  <Textarea
                    value={articleForm.content}
                    onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })}
                    placeholder="Rédigez votre article..."
                    rows={10}
                    className="mt-2"
                    data-testid="article-input-content"
                  />
                </div>
                <button
                  type="submit"
                  disabled={savingArticle}
                  className="inline-flex items-center gap-2 rounded-full px-7 py-3 bg-[#1b2653] text-white text-sm font-semibold hover:bg-[#2f6fd8] transition-colors duration-300 disabled:opacity-60"
                  data-testid="article-save-btn"
                >
                  {savingArticle ? "Enregistrement..." : editingId ? "Mettre à jour" : "Publier l'article"}
                </button>
              </form>
            )}

            {loading ? (
              <p className="text-slate-500 font-light">Chargement...</p>
            ) : articles.length === 0 ? (
              <div className="bg-white rounded-lg border border-slate-200 p-14 text-center">
                <Newspaper size={40} className="text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 font-light">Aucun article publié.</p>
              </div>
            ) : (
              <div className="space-y-5" data-testid="articles-list">
                {articles.map((a) => (
                  <div key={a.id} className="bg-white rounded-lg border border-slate-200 p-6 hover:shadow-md transition-shadow duration-300">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex gap-5 flex-1 min-w-0">
                        {a.image && (
                          <img src={a.image} alt={a.title} className="w-24 h-24 rounded-md object-cover shrink-0 hidden sm:block" />
                        )}
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-3 mb-2">
                            {a.category && (
                              <Badge variant="outline" className="border-[#2f6fd8]/30 text-[#2547a8] bg-[#eef3fc]">{a.category}</Badge>
                            )}
                            <span className="text-slate-400 text-xs">{formatDateTime(a.created_at)}</span>
                          </div>
                          <h3 className="font-semibold text-[#1b2653] mb-1.5">{a.title}</h3>
                          <p className="text-slate-500 text-sm font-light truncate">{a.content.slice(0, 120)}...</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => openEditArticle(a)}
                          className="p-2.5 rounded-md text-slate-400 hover:text-[#2547a8] hover:bg-[#eef3fc] transition-colors duration-300"
                          aria-label="Modifier"
                          data-testid={`edit-article-${a.id}`}
                        >
                          <Pencil size={17} />
                        </button>
                        <button
                          onClick={() => deleteArticle(a.id)}
                          className="p-2.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors duration-300"
                          aria-label="Supprimer"
                          data-testid={`delete-article-${a.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* CANDIDATURES */}
          <TabsContent value="careers" className="mt-8">
            {loading ? (
              <p className="text-slate-500 font-light">Chargement...</p>
            ) : applications.length === 0 ? (
              <div className="bg-white rounded-lg border border-slate-200 p-14 text-center">
                <Briefcase size={40} className="text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 font-light">Aucune candidature reçue pour le moment.</p>
              </div>
            ) : (
              <div className="space-y-5" data-testid="applications-list">
                {applications.map((a) => (
                  <div key={a.id} className="bg-white rounded-lg border border-slate-200 p-6 hover:shadow-md transition-shadow duration-300">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <span className="font-semibold text-[#1b2653]">{a.name}</span>
                          <Badge variant="outline" className="border-[#2f6fd8]/30 text-[#2547a8] bg-[#eef3fc]">{a.position}</Badge>
                          <span className="text-slate-400 text-xs">{formatDateTime(a.created_at)}</span>
                        </div>
                        <div className="text-sm text-slate-500 mb-2">
                          <a href={`mailto:${a.email}`} className="hover:text-[#2547a8] transition-colors">{a.email}</a>
                          {a.phone && <span> • {a.phone}</span>}
                        </div>
                        {a.message && (
                          <p className="text-slate-700 text-[14px] leading-relaxed font-light whitespace-pre-wrap">{a.message}</p>
                        )}
                        {!a.cv_filename && (
                          <p className="text-slate-400 text-xs mt-2 italic">Aucun CV joint</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {a.cv_filename && (
                          <button
                            onClick={() => downloadCv(a)}
                            className="inline-flex items-center gap-2 rounded-full px-5 py-2 bg-[#1b2653] text-white text-sm font-medium hover:bg-[#2f6fd8] transition-colors duration-300"
                            data-testid={`download-cv-${a.id}`}
                          >
                            <FileDown size={15} /> CV
                          </button>
                        )}
                        <button
                          onClick={() => deleteApplication(a.id)}
                          className="p-2.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors duration-300"
                          aria-label="Supprimer"
                          data-testid={`delete-application-${a.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
